import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Package,
  Plus,
  QrCode,
  ExternalLink,
  Loader2,
  X,
  Folder,
  Tag,
  Trash2,
} from 'lucide-react';
import QRCode from 'react-qr-code';

const BusinessProducts = ({
  businessId,
  businessCategory,
  readOnly = false,
}) => {
  const [products, setProducts] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    categoryId: '',
  });
  const [newCategory, setNewCategory] = useState({ name: '', parentId: '' });
  const [adding, setAdding] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null); // For QR modal
  const server_url = process.env.REACT_APP_API_URI;
  const token = localStorage.getItem('loginToken');

  useEffect(() => {
    fetchProducts();
    fetchBusinessCategories();
  }, [businessId]);

  const fetchBusinessCategories = async () => {
    try {
      const response = await axios.get(
        `${server_url}/businessCategory/${businessId}`,
      );
      setBusinessCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching business categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${server_url}/my-products/getMyProductsByUserId`,
        {
          userId: businessId,
        },
      );
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name) return;
    try {
      await axios.post(`${server_url}/businessCategory/add`, {
        businessId,
        name: newCategory.name,
        parentId: newCategory.parentId || null,
      });
      setNewCategory({ name: '', parentId: '' });
      fetchBusinessCategories();
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?'))
      return;
    try {
      await axios.delete(`${server_url}/businessCategory/${id}`);
      fetchBusinessCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.description) return;

    setAdding(true);
    try {
      const categoryRes = await axios.get(
        `${server_url}/context/get-all-category`,
      );
      const categories = categoryRes.data.data;

      const matchedCat =
        categories.find((c) =>
          businessCategory.some(
            (bc) => bc.toLowerCase() === c.title.toLowerCase(),
          ),
        ) || categories[0];

      const formData = {
        contentName: newProduct.name,
        contentDescription: newProduct.description,
        userId: businessId,
        catId: matchedCat?._id || 'business_product',
        businessCategoryId: newProduct.categoryId || null,
        timestamp: new Date(),
      };

      await axios.post(`${server_url}/my-products/addProduct`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNewProduct({ name: '', description: '', categoryId: '' });
      setShowAddModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Failed to add product');
    } finally {
      setAdding(false);
    }
  };

  const getQRLink = (product) => {
    return `${window.location.origin}/company-rating/${businessId}?product=${product.contentId}`;
  };

  const renderCategoryOptions = (parentId = null, prefix = '') => {
    return businessCategories
      .filter((c) => c.parentId === (parentId || null))
      .map((c) => (
        <React.Fragment key={c._id}>
          <option value={c._id}>
            {prefix}
            {c.name}
          </option>
          {renderCategoryOptions(c._id, prefix + '— ')}
        </React.Fragment>
      ));
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-400">
          <Package className="w-4 h-4" />
          <span className="text-xs font-black uppercase tracking-[0.2em]">
            Product Portfolio
          </span>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all border border-slate-700/50"
            >
              <Folder className="w-4 h-4" />
              Manage Categories
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-all shadow-lg shadow-blue-900/20"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        )}
      </div>

      {products.length === 0 ? (
        <div className="bg-slate-800/20 border border-slate-700/30 rounded-3xl p-12 text-center">
          <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-white font-bold text-lg mb-2">No Products Yet</h3>
          <p className="text-slate-400 text-sm max-w-xs mx-auto">
            {readOnly
              ? "This business hasn't listed any products yet."
              : 'Start adding products to generate specialized QR codes for your customers to rate.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {products.map((product) => (
            <div
              key={product._id}
              className="group bg-slate-800/40 border border-slate-700/30 rounded-2xl p-5 hover:bg-slate-800/60 transition-all border-l-4 border-l-transparent hover:border-l-blue-500"
            >
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-white font-bold text-lg">
                  {product.contentName}
                </h4>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 rounded-lg">
                    <span className="text-xs font-black">
                      ★ {product.averageRating || '0.0'}
                    </span>
                  </div>
                  <div className="px-2.5 py-1 bg-slate-700/30 text-slate-400 border border-slate-700/30 rounded-lg">
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      {product.totalReviews || 0} reviews
                    </span>
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                      <button
                        onClick={() => setSelectedProduct(product)}
                        className="p-2 bg-slate-700/50 hover:bg-blue-500/20 hover:text-blue-400 text-slate-400 rounded-lg transition-all"
                        title="View QR Code"
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4 line-clamp-2 italic">
                {product.contentDescription}
              </p>
              {product.businessCategoryId && (
                <div className="flex items-center gap-1.5 mb-4 text-[10px] font-bold text-blue-400 bg-blue-400/10 w-fit px-2 py-0.5 rounded-md border border-blue-400/20">
                  <Tag className="w-3 h-3" />
                  {product.businessCategoryId.name}
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t border-slate-700/30">
                <span className="text-[10px] text-slate-500 uppercase font-black tracking-widest">
                  Content ID: {product.contentId}
                </span>
                {readOnly && (
                  <a
                    href={getQRLink(product)}
                    target="_self"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 text-xs font-bold flex items-center gap-1"
                  >
                    Rate This <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setShowAddModal(false)}
          ></div>
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <button
              onClick={() => setShowAddModal(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-bold text-white mb-6">New Product</h3>
            <form onSubmit={handleAddProduct} className="space-y-4">
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">
                  Product Name
                </label>
                <input
                  type="text"
                  required
                  value={newProduct.name}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, name: e.target.value })
                  }
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                  placeholder="e.g. Signature Burger"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">
                  Category (Optional)
                </label>
                <select
                  value={newProduct.categoryId}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, categoryId: e.target.value })
                  }
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium appearance-none"
                >
                  <option value="">No Category</option>
                  {renderCategoryOptions()}
                </select>
              </div>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">
                  Description
                </label>
                <textarea
                  required
                  value={newProduct.description}
                  onChange={(e) =>
                    setNewProduct({
                      ...newProduct,
                      description: e.target.value,
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium h-32"
                  placeholder="Tell your customers about this product..."
                />
              </div>
              <button
                type="submit"
                disabled={adding}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white font-black py-4 rounded-xl transition-all shadow-xl shadow-blue-500/20 flex items-center justify-center gap-2"
              >
                {adding ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Plus className="w-5 h-5" />
                )}
                Add to Portfolio
              </button>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setSelectedProduct(null)}
          ></div>
          <div className="relative bg-white rounded-3xl p-10 max-w-sm w-full shadow-2xl text-center">
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="mb-6 flex flex-col items-center">
              <div className="p-4 bg-slate-50 rounded-2xl mb-4">
                <QRCode value={getQRLink(selectedProduct)} size={200} />
              </div>
              <h3 className="text-slate-900 font-black text-2xl">
                {selectedProduct.contentName}
              </h3>
              <p className="text-slate-500 text-sm mt-1">Review QR Code</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => window.print()}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition-all"
              >
                Print QR Code
              </button>
              <p className="text-[10px] text-slate-400 font-medium">
                Print this and place it on your product packaging or menu.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setShowCategoryModal(false)}
          ></div>
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-lg w-full shadow-2xl max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setShowCategoryModal(false)}
              className="absolute top-4 right-4 text-slate-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-bold text-white mb-6">
              Product Categories
            </h3>

            <form onSubmit={handleAddCategory} className="space-y-4 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newCategory.name}
                    onChange={(e) =>
                      setNewCategory({ ...newCategory, name: e.target.value })
                    }
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                    placeholder="e.g. Burgers"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Parent Category
                  </label>
                  <select
                    value={newCategory.parentId}
                    onChange={(e) =>
                      setNewCategory({
                        ...newCategory,
                        parentId: e.target.value,
                      })
                    }
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium appearance-none"
                  >
                    <option value="">None (Top Level)</option>
                    {renderCategoryOptions()}
                  </select>
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-slate-700 hover:bg-blue-600 text-white font-bold py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Create Category
              </button>
            </form>

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">
                Existing Categories
              </label>
              {businessCategories.length === 0 ? (
                <p className="text-slate-500 text-sm italic">
                  No categories created yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {businessCategories.map((cat) => (
                    <div
                      key={cat._id}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl border border-slate-700/30"
                    >
                      <div className="flex items-center gap-3">
                        <Folder className="w-4 h-4 text-blue-400/60" />
                        <div>
                          <p className="text-white font-bold text-sm">
                            {cat.name}
                          </p>
                          {cat.parentId && (
                            <p className="text-[10px] text-slate-500 flex items-center gap-1">
                              Sub of{' '}
                              <span className="text-slate-400">
                                {businessCategories.find(
                                  (p) => p._id === cat.parentId,
                                )?.name || 'Unknown'}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteCategory(cat._id)}
                        className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProducts;
